import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronRight, Receipt } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatEventTime, parseUTCDate } from '@/lib/utils'
import type { ActionItemLike } from '@/features/purchase-orders/action-audit-model'
import type { MessageDraftUpdate, POCommunicationThread, ShipmentDocOption } from '@/features/communications/types'
import { useActionEmail } from '@/features/communications/action-email'
import {
  ThreadViewWithReply,
  type ThreadContextOptions,
  type ThreadInvoice,
} from '@/features/communications/po-thread'
import { ThreadAnalysisPill } from '@/features/communications/analysis-status'
import { ConversationListItemContent, ConversationSearch } from '@/features/communications/conversation-list'
import { conversationListItemClass } from '@/features/communications/message-format'
import {
  highlight,
  isAiMessage,
  messageMatches,
  normalizeQuery,
} from '@/features/communications/thread-display'

function isClosedThread(thread: POCommunicationThread) {
  return thread.status === 'resolved' || thread.status === 'archived'
}

function threadMatches(thread: POCommunicationThread, query: string) {
  return (
    thread.subject.toLowerCase().includes(query) ||
    (thread.recipient_email ?? '').toLowerCase().includes(query) ||
    (thread.recipient_name ?? '').toLowerCase().includes(query) ||
    (thread.messages ?? []).some((m) => messageMatches(m, query))
  )
}

/** One row in the thread list: what the thread is and who it is with; closed threads are muted. */
function ThreadListItem({
  thread,
  invoice,
  selected,
  query,
  onSelect,
}: {
  thread: POCommunicationThread
  invoice: ThreadInvoice | undefined
  selected: boolean
  query: string
  onSelect: () => void
}) {
  const messages = (thread.messages ?? []).filter((m) => !isAiMessage(m))
  const matches = query ? messages.filter((m) => messageMatches(m, query)).length : 0
  const closed = isClosedThread(thread)
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        title={thread.subject}
        data-testid={`thread-list-item-${thread.uuid}`}
        className={cn(
          conversationListItemClass(selected),
          // Stacked above the open thread (narrow container): subject on one line, then one
          // wrapping line of who, when and counts.
          '@max-2xl/conversations:flex @max-2xl/conversations:flex-wrap @max-2xl/conversations:items-center @max-2xl/conversations:gap-x-2 @max-2xl/conversations:gap-y-0.5 @max-2xl/conversations:space-y-0 @max-2xl/conversations:px-2 @max-2xl/conversations:py-1.5',
        )}
      >
        <ConversationListItemContent
          selected={selected}
          muted={closed}
          title={highlight(thread.subject, query)}
          meta={
            <>
              <span className="min-w-0 truncate">
                {thread.recipient_name || thread.recipient_email}
              </span>
              <span aria-hidden>·</span>
              <time dateTime={thread.last_activity_at} className="shrink-0 tabular-nums">
                {formatEventTime(thread.last_activity_at)}
              </time>
            </>
          }
          badges={
            <>
              {invoice && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground/80">
                  <Receipt aria-hidden className="h-3 w-3" />
                  Invoice {invoice.number}
                </span>
              )}
              <span className="text-[11px] text-muted-foreground">
                {messages.length} message{messages.length === 1 ? '' : 's'}
              </span>
              <ThreadAnalysisPill analyses={thread.analysis} />
              {matches > 0 && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
                  {matches} {matches === 1 ? 'match' : 'matches'}
                </span>
              )}
            </>
          }
        />
      </button>
    </li>
  )
}

/** Every conversation on a purchase order: a thread list beside the one thread being read. */
export function POConversations({
  initialThreadUuid,
  threads,
  isLoading,
  actions,
  poUuid,
  hasPoPdf,
  hasQuotePdf,
  shipmentDocOptions,
  onApprove,
  onReject,
  onSend,
  onUpdate,
  newThreadAction,
  emptyState,
  invoiceForThread,
  contextForThread,
  title = 'Communications',
  hideResolved = false,
}: {
  /** Open this thread first and bring the conversations into view. */
  initialThreadUuid?: string
  /** The PO's own threads and the threads on its linked invoices, in any order. */
  threads: POCommunicationThread[] | undefined
  isLoading: boolean
  actions: ActionItemLike[]
  poUuid?: string
  hasPoPdf: boolean
  hasQuotePdf: boolean
  shipmentDocOptions: ShipmentDocOption[]
  onApprove: (uuid: string) => void
  onReject: (uuid: string) => void
  onSend: (uuid: string) => void
  onUpdate: (uuid: string, body: MessageDraftUpdate) => void
  newThreadAction: ReactNode
  emptyState: ReactNode
  /** The invoice a thread belongs to, for threads from the accounting mailbox. */
  invoiceForThread?: (thread: POCommunicationThread) => ThreadInvoice | undefined
  /** Quote Builder conversations (USA-1094): greeting, sign-off, recording and badge per thread. */
  contextForThread?: (thread: POCommunicationThread) => ThreadContextOptions | undefined
  title?: string
  /** The PO page lists resolved threads in a folded group below the open ones (USA-1432). */
  hideResolved?: boolean
}) {
  const [rawQuery, setRawQuery] = useState('')
  // A thread picked by hand, until an action card links to a different email.
  const [selection, setSelection] = useState<{ id: number; focus?: number } | null>(null)
  // Opening or folding the resolved group by hand, until an action card links to a different email.
  const [resolvedChoice, setResolvedChoice] = useState<{ show: boolean; focus?: number } | null>(
    null,
  )
  const sectionRef = useRef<HTMLDivElement>(null)
  // An action item card's email link opens the thread holding that email (USA-1417).
  const { focusMessageId } = useActionEmail()
  const requested = initialThreadUuid
    ? threads?.find((thread) => thread.uuid === initialThreadUuid)
    : focusMessageId != null
      ? threads?.find((thread) => thread.messages?.some((message) => message.id === focusMessageId))
      : undefined
  const requestedId = requested?.id
  const selectedId = selection && selection.focus === focusMessageId ? selection.id : null
  useEffect(() => {
    if (requestedId != null && focusMessageId == null) sectionRef.current?.scrollIntoView({ block: 'start' })
  }, [requestedId, focusMessageId])
  const query = normalizeQuery(rawQuery)

  const sorted = [...(threads ?? [])].sort(
    (a, b) =>
      parseUTCDate(b.last_activity_at).getTime() - parseUTCDate(a.last_activity_at).getTime(),
  )
  // Search covers every thread, open and resolved.
  const visible = query ? sorted.filter((t) => threadMatches(t, query)) : sorted
  // Until someone picks a thread, show the one most likely to need attention: the newest open one.
  const primaryId = (sorted.find((t) => !isClosedThread(t)) ?? sorted[0])?.id
  const wanted = selectedId ?? requestedId ?? primaryId
  const shownId = visible.some((t) => t.id === wanted) ? wanted : visible[0]?.id
  // The PO page lists resolved threads in a folded group below the open ones (USA-1432). It opens
  // by itself when the thread on screen is resolved (a link to one, or nothing else is open).
  const listedOpen = hideResolved ? visible.filter((t) => !isClosedThread(t)) : visible
  const listedResolved = hideResolved ? visible.filter(isClosedThread) : []
  const shownClosed = listedResolved.some((t) => t.id === shownId)
  const resolvedOpen =
    resolvedChoice && resolvedChoice.focus === focusMessageId ? resolvedChoice.show : shownClosed || !!query
  const hasThreads = sorted.length > 0

  return (
    <div ref={sectionRef} className="@container/po-comms scroll-mt-4 space-y-3">
      {/* Too narrow for one row (phone, tablet): title, toggle and New Thread, search below. */}
      <div className="flex flex-wrap items-center gap-2 max-sm:gap-x-1.5">
        <h3 className="mr-auto text-xs font-medium uppercase tracking-wider text-muted-foreground max-sm:tracking-wide">
          {title}
        </h3>
        {hasThreads && (
          <ConversationSearch
            value={rawQuery}
            onChange={setRawQuery}
            className="order-last w-full sm:order-none sm:w-72 @max-2xl/po-comms:order-last @max-2xl/po-comms:w-full"
          />
        )}
        <span className="contents max-sm:[&>button]:px-2">{newThreadAction}</span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : !hasThreads ? (
        emptyState
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            No messages match “{rawQuery.trim()}”
          </p>
          <Button size="sm" variant="outline" onClick={() => setRawQuery('')}>
            Clear search
          </Button>
        </div>
      ) : (
        <div className="@container/conversations">
          <div
            className={cn(
              'rounded-lg border bg-card',
              visible.length > 1 && '@2xl/conversations:grid @2xl/conversations:grid-cols-[15rem_minmax(0,1fr)]',
            )}
          >
            {visible.length > 1 && (
              <nav
                aria-label="Threads"
                className="rounded-t-lg border-b bg-muted/40 p-1.5 @max-2xl/conversations:p-1 @2xl/conversations:rounded-tr-none @2xl/conversations:rounded-bl-lg @2xl/conversations:border-b-0 @2xl/conversations:border-r"
              >
                <ul className="space-y-1">
                  {listedOpen.map((thread) => (
                    <ThreadListItem
                      key={thread.id}
                      thread={thread}
                      invoice={invoiceForThread?.(thread)}
                      selected={thread.id === shownId}
                      query={query}
                      onSelect={() => setSelection({ id: thread.id, focus: focusMessageId })}
                    />
                  ))}
                </ul>
                {listedResolved.length > 0 && (
                  <div className={cn(listedOpen.length > 0 && 'mt-1.5 border-t pt-1.5')}>
                    <button
                      type="button"
                      aria-expanded={resolvedOpen}
                      aria-controls="po-resolved-threads"
                      onClick={() => setResolvedChoice({ show: !resolvedOpen, focus: focusMessageId })}
                      className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                    >
                      <ChevronRight
                        aria-hidden
                        className={cn('size-3.5 shrink-0 transition-transform', resolvedOpen && 'rotate-90')}
                      />
                      Resolved ({listedResolved.length})
                    </button>
                    {resolvedOpen && (
                      <ul id="po-resolved-threads" className="mt-1 space-y-1">
                        {listedResolved.map((thread) => (
                          <ThreadListItem
                            key={thread.id}
                            thread={thread}
                            invoice={invoiceForThread?.(thread)}
                            selected={thread.id === shownId}
                            query={query}
                            onSelect={() => setSelection({ id: thread.id, focus: focusMessageId })}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </nav>
            )}
            {/* Every thread stays mounted so a half-written reply survives switching threads. */}
            {sorted.map((thread) => (
              <div key={thread.id} hidden={thread.id !== shownId} className="min-w-0">
                <ThreadViewWithReply
                  thread={thread}
                  invoice={invoiceForThread?.(thread)}
                  context={contextForThread?.(thread)}
                  actions={actions}
                  collapsible={false}
                  query={query}
                  poUuid={poUuid}
                  hasPoPdf={hasPoPdf}
                  hasQuotePdf={hasQuotePdf}
                  shipmentDocOptions={shipmentDocOptions}
                  onApprove={onApprove}
                  onReject={onReject}
                  onSend={onSend}
                  onUpdate={onUpdate}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
